import { Discount } from "./discount"
import { ServiceAction } from "./service-part-action"

/**
 * The parts of an evaluated component the "dirty" test looks at.
 *
 * Declared structurally so both inspection flows satisfy it without any
 * conversion: the job inspection's `EvaluationPartForm` and the bike-for-sale
 * inspection's `BikeForSaleInspectionPartFormValue`. Same pattern as
 * `PartBikeConfigRelevance` in `service-bike-part.ts`.
 */
export interface EvaluatedPartDirtiness {
  /**
   * Set the moment the mechanic interacts with the component's card. This is
   * the only signal that catches a grade-only edit — the untouched default
   * grade differs between reception and inspection, so a grade can't be told
   * apart from a seeded one by looking at the value.
   */
  isDirty: boolean
  recommendedAction: ServiceAction | null
  comment?: string | null
  products: { id: string }[]
  productChoices?: { id: string }[]
  productGroups?: { products: { id: string }[] }[]
  laborPriceDiscount?: Discount | null
  damperBrandId?: string | null
}

/**
 * Whether the mechanic has put anything into this component.
 *
 * Used when the bike specification changes mid-inspection: components that no
 * longer fit the corrected spec are dropped, but only when they are not dirty
 * — a component the mechanic actually graded, commented or gave an action must
 * survive a spec correction.
 *
 * The content checks below are a fallback for components that predate the
 * `isDirty` flag (parts carried over from the reception, and inspection drafts
 * saved before the flag existed).
 */
export const isEvaluatedPartDirty = (part: EvaluatedPartDirtiness): boolean => {
  if (part.isDirty) return true

  if (
    part.recommendedAction !== null &&
    part.recommendedAction !== ServiceAction.DO_NOTHING
  ) {
    return true
  }

  if ((part.comment ?? "").trim().length > 0) return true

  if (part.products.length > 0) return true
  if (part.productChoices && part.productChoices.length > 0) return true
  if (part.productGroups?.some((group) => group.products.length > 0)) {
    return true
  }

  if (part.laborPriceDiscount != null) return true
  if (part.damperBrandId != null) return true

  return false
}
